const noop = Function.prototype
const isCanceled = { isCanceled: true }

/**
 * @typedef {Object} CancellablePromise
 * @property {Promise} promise - The original promise to subscribe to (then, catch)
 * @property {Function} cancel - The method to cancel the promise
 * @property {Function} isCanceled - Returns true if the promises has been cancelled, false otherwise
 */

/**
 * Makes the given Promise cancellable.
 * Implementation based upon {@link https://github.com/facebook/react/issues/5465#issuecomment-157888325}
 *
 * @example
 * const cancelable = makeCancelable(somePromise)
 *
 * // Cancel promise
 * cancelable.cancel()
 *
 * // Access promise
 * cancelable
 *     .promise
 *     .then(() => console.log('resolved'))
 *
 * @param {Promise} promise - The promise to make cancellable
 * @param {Function} [catchCallback=noop] - Handler for the wrapper rejection
 * @returns {CancellablePromise} The cancellable promise
 */
export function makeCancelable(promise, catchCallback = noop) {
    let hasCanceled = false

    return {
        // Catching here to avoid "uncaught promise rejection errors"
        cancel() {
            hasCanceled = true
        },
        isCanceled() {
            return hasCanceled
        },
        promise: new Promise((resolve, reject) => {
            promise
                .then(value => {
                    hasCanceled ? reject(isCanceled) : resolve(value)
                })
                .catch(error => {
                    hasCanceled ? reject(isCanceled) : reject(error)
                })
        }).catch(catchCallback)
    }
}

export function makeCancelableAll() {
    const _promises = []
    const func = promise => {
        const wrappedPromise = makeCancelable(promise)
        _promises.push(wrappedPromise)
        return wrappedPromise.promise
    }
    func.cancelAll = () => {
        _promises.forEach(p => p.cancel())
    }

    return func
}
